'use client';

import { Portal, Root, Trigger, Content } from '@radix-ui/react-popover';
import { cx } from '@onbeam/business-styled-system/css';
import { useEffect, useRef } from 'react';
import { PopoverProps } from './Popover.types';
import { popover } from './Popover.styles';

export const Popover = ({
  children,
  open,
  onOpenChange,
  trigger,
  align = 'start',
  side = 'bottom',
  sideOffset = 8,
  className,
  onInteractOutside,
  ...props
}: PopoverProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLElement>(null);
  const classes = popover();

  useEffect(() => {
    if (!triggerRef.current) return;
    const rootNode = triggerRef.current.getRootNode();
    // When inside shadow dom, place the popover in the shadow root instead of the document body
    if (rootNode.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      rootRef.current = triggerRef.current.getRootNode() as HTMLElement;
    }
  }, []);

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Trigger ref={triggerRef} asChild>
        {trigger ?? <div aria-hidden className={classes.hidden} />}
      </Trigger>
      <Portal>
        <Content
          collisionPadding={4}
          sideOffset={sideOffset}
          align={align}
          side={side}
          {...props}
          className={cx(classes.content, className)}
          // Radix already handles this logic, but not for triggers within the Shadow DOM
          // Using `composedPath` we can find the trigger within the Shadow DOM
          onInteractOutside={(e) => {
            const path = e.detail.originalEvent.composedPath();
            const targetIsTrigger = path.some(
              (el) => el instanceof Node && triggerRef.current?.contains(el),
            );
            if (targetIsTrigger) e.preventDefault();
            onInteractOutside?.(e);
          }}
          {...props}
        >
          {children}
        </Content>
      </Portal>
    </Root>
  );
};
